import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * Spec 033 US3 — admin-observability DTOs.
 *
 * Shapes match `specs/033-admin-observability/contracts/admin-api.md`.
 * Wire-format only — server-side aggregation happens in the service.
 */

// ── Dashboard response ────────────────────────────────────────────────

/**
 * WHY EVERY COST ON THIS WIRE IS IN MILLI-CENTS (1/1000 of a USD cent).
 *
 * A single AI call costs 13-75 milli-cents. Convert to whole cents anywhere
 * before the last formatting step and the number becomes 0 — which is exactly
 * what happened: on 2026-09-02 the admin dashboard reported $0.00 against 4,787
 * real Groq calls in 24h, because the server summed a per-row cent column that
 * had itself been rounded to zero on every row.
 *
 * So the unit stays milli-cents for the whole pipe — log row → SUM → DTO — and
 * is divided by 100,000 exactly once, in the client formatter. Anything that
 * adds a cost field here must follow the same rule.
 */
export const providerAggregateSchema = z.object({
  llm: z.object({ costMilliCents: z.number().int(), requestCount: z.number().int() }),
  proxy: z.object({ costMilliCents: z.number().int(), requestCount: z.number().int() }),
});

const profileScanSchema = z.object({
  profileId: z.string(),
  profileName: z.string(),
  userId: z.string(),
  lastScannedAt: z.date().nullable(),
  jobCount: z.number().int(),
  status: z.enum(["success", "failed", "never"]),
  errorMessage: z.string().optional(),
});

export const dashboardResponseSchema = z.object({
  /** Active jobs first discovered in the rolling 24h; rescans do not count again. */
  jobsFound24h: z.number().int().nonnegative(),
  today: providerAggregateSchema,
  month: providerAggregateSchema,
  topOperations: z.array(
    z.object({
      operation: z.string(),
      countToday: z.number().int(),
      costMilliCentsToday: z.number().int(),
      costMilliCentsThisMonth: z.number().int(),
    }),
  ),
  // Caps are configured in cents but travel in milli-cents like everything
  // else in this payload. One row mixing two units is how a "spend vs cap"
  // comparison silently becomes a 1000x error.
  topUsers: z.array(
    z.object({
      userId: z.string(),
      email: z.string(),
      aiSpendMilliCentsToday: z.number().int(),
      aiSpendMilliCentsThisMonth: z.number().int(),
      proxySpendMilliCentsToday: z.number().int(),
      proxySpendMilliCentsThisMonth: z.number().int(),
      aiCapMilliCentsPerDay: z.number().int(),
      aiCapMilliCentsPerMonth: z.number().int(),
      capStatus: z.enum(["ok", "warning", "blocked"]),
    }),
  ),
  unpriced: z.object({
    groqCount: z.number().int(),
    proxyCount: z.number().int(),
  }),
  queueHealth: z.object({
    submissionDepth: z.number().int(),
    submissionFailed: z.number().int(),
    tailoringDepth: z.number().int(),
    tailoringFailed: z.number().int(),
  }),
  /** Disposable tailored-PDF cache pressure; never contains resume content. */
  pdfCache: z.object({
    enabled: z.boolean(),
    globalBytes: z.number().int().nonnegative(),
    globalLimitBytes: z.number().int().nonnegative(),
    globalPercent: z.number().nonnegative(),
    objectCount: z.number().int().nonnegative(),
    highWatermarkPercent: z.number().nonnegative(),
    lowWatermarkPercent: z.number().nonnegative(),
    maxUserBytes: z.number().int().nonnegative(),
    diskReserveBytes: z.number().int().nonnegative(),
    freeDiskBytes: z.number().int().nonnegative().nullable(),
    telemetry: z.object({
      hits: z.number().int().nonnegative(),
      misses: z.number().int().nonnegative(),
      bypasses: z.number().int().nonnegative(),
      evictions: z.object({
        count: z.number().int().nonnegative(),
        bytes: z.number().int().nonnegative(),
      }),
      renderDurationMs: z.number().int().nonnegative(),
    }),
  }),
  profileScans: z.array(profileScanSchema),
  sentryErrors: z.object({
    lastHour: z.number().int(),
    last24Hours: z.number().int(),
  }),
});

export type DashboardResponse = z.infer<typeof dashboardResponseSchema>;
export type ProviderAggregate = z.infer<typeof providerAggregateSchema>;

// ── User analytics (per-profile + per-user rollup) ────────────────────
// Admin daily-review surface: every job profile (and a per-user rollup) with
// jobs discovered in a trailing window and the owner's last-active timestamp
// (latest `Session.lastSeenAt`). Dates are ISO strings on the wire.

export const profileAnalyticsRowSchema = z.object({
  profileId: z.string(),
  profileName: z.string(),
  userId: z.string(),
  userEmail: z.string(),
  /** Jobs discovered for this profile within the trailing window. */
  jobsInWindow: z.number().int(),
  /** Latest `Session.lastSeenAt` for the owning user, ISO; null if never seen. */
  lastActiveAt: z.string().nullable(),
  isActive: z.boolean(),
});

export const userAnalyticsTierSchema = z.enum(["FREE", "PRO", "ULTIMATE"]);

/** Where the user is in the in-house / card trial right now. */
export const userAnalyticsTrialStatusSchema = z.enum(["trialing", "expired", "none"]);

export const userAnalyticsRowSchema = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string(),
  /** Account creation time, ISO. */
  signedUpAt: z.string(),
  /**
   * Tier that gates features right now: a TRIALING subscription whose
   * `lsCurrentPeriodEnd` has passed counts as FREE (see `resolveEntitlement`).
   */
  effectiveTier: userAnalyticsTierSchema,
  trialStatus: userAnalyticsTrialStatusSchema,
  onboardingCompleted: z.boolean(),
  onboardingStep: z.string().nullable(),
  /** 0 for users who never created a job profile. */
  profileCount: z.number().int(),
  /** Sum of jobs discovered across all the user's profiles in the window. */
  jobsInWindow: z.number().int(),
  /** Latest `JobProfile.lastViewedAt` across the user's profiles; null if never opened a job. */
  lastOpenedJobAt: z.string().nullable(),
  lastActiveAt: z.string().nullable(),
});

export const userAnalyticsResponseSchema = z.object({
  /** When the snapshot was computed (ISO). */
  generatedAt: z.string(),
  /** Trailing window in days the `jobsInWindow` counts cover (e.g. 7). */
  windowDays: z.number().int(),
  /** Job profiles of real users, newest first, capped server-side. */
  byProfile: z.array(profileAnalyticsRowSchema),
  /** Real-user profiles in total, so the client can say "showing N of M". */
  byProfileTotal: z.number().int(),
  /** One page of real users (newest signup first), with or without a profile. */
  byUser: z.array(userAnalyticsRowSchema),
  /** Real users in total across all pages. */
  byUserTotal: z.number().int(),
  /** 1-based page index of `byUser`. */
  page: z.number().int(),
  pageSize: z.number().int(),
});

export type ProfileAnalyticsRow = z.infer<typeof profileAnalyticsRowSchema>;
export type UserAnalyticsRow = z.infer<typeof userAnalyticsRowSchema>;
export type UserAnalyticsTrialStatus = z.infer<typeof userAnalyticsTrialStatusSchema>;
export type UserAnalyticsResponse = z.infer<typeof userAnalyticsResponseSchema>;

// ── Per-user drill-down response ──────────────────────────────────────

export const userRequestsResponseSchema = z.object({
  groqRequests: z.array(
    z.object({
      id: z.string(),
      operation: z.string().nullable(),
      model: z.string(),
      promptTokens: z.number().int().nullable(),
      completionTokens: z.number().int().nullable(),
      costMilliCents: z.number().int().nullable(),
      status: z.string(),
      createdAt: z.string(),
    }),
  ),
  proxyRequests: z.array(
    z.object({
      id: z.string(),
      board: z.string(),
      provider: z.string(),
      requestCount: z.number().int(),
      bytes: z.number().int().nullable(),
      costMilliCents: z.number().int().nullable(),
      status: z.string(),
      createdAt: z.string(),
    }),
  ),
  nextCursor: z.string().nullable(),
});

export type UserRequestsResponse = z.infer<typeof userRequestsResponseSchema>;

// ── Cap-override request / response ───────────────────────────────────

// Sanity ceiling — admin can't accidentally set a $100/day cap.
const MAX_OVERRIDE_CENTS = 1_000_000;

export const updateCapsSchema = z.object({
  aiOverrideCapCentsPerDay: z.number().int().min(0).max(MAX_OVERRIDE_CENTS).nullable().optional(),
  aiOverrideCapCentsPerMonth: z.number().int().min(0).max(MAX_OVERRIDE_CENTS).nullable().optional(),
  proxyOverrideCapCentsPerDay: z
    .number()
    .int()
    .min(0)
    .max(MAX_OVERRIDE_CENTS)
    .nullable()
    .optional(),
  proxyOverrideCapCentsPerMonth: z
    .number()
    .int()
    .min(0)
    .max(MAX_OVERRIDE_CENTS)
    .nullable()
    .optional(),
  reason: z.string().trim().max(500).optional(),
});

export class UpdateCapsDto extends createZodDto(updateCapsSchema) {}

export type UpdateCapsResponse = {
  userId: string;
  appliedAt: string;
  current: {
    aiOverrideCapCentsPerDay: number | null;
    aiOverrideCapCentsPerMonth: number | null;
    proxyOverrideCapCentsPerDay: number | null;
    proxyOverrideCapCentsPerMonth: number | null;
  };
};

// ── Spend trend (US5) ─────────────────────────────────────────────────

export const spendTrendQuerySchema = z.object({
  period: z.enum(["30d", "90d"]).default("30d"),
});

export type SpendTrendBucket = {
  date: string;
  llmCostMilliCents: number;
  proxyCostMilliCents: number;
};

export type SpendTrendResponse = {
  period: "30d" | "90d";
  buckets: SpendTrendBucket[];
};

// ── Job retention stats (Spec 034 US3) ────────────────────────────────
// Shape matches `specs/034-job-retention-soft-delete/contracts/job-retention-stats.md`.

const jobRetentionPerBoardCounts = z.record(
  z.string(), // JobBoard
  z.object({
    proposed: z.number().int(),
    archived: z.number().int(),
    hardDeleted: z.number().int(),
    protected: z.number().int(),
  }),
);

const jobRetentionRollup = z.object({
  proposed: z.number().int(),
  archived: z.number().int(),
  hardDeleted: z.number().int(),
  protected: z.number().int(),
  runs: z.number().int(),
});

const unseenProposalBoard = z.object({
  eligibleUnseen: z.number().int(),
  protectedByApplication: z.number().int(),
  protectedByRecentLive: z.number().int(),
  boardCoverageOk: z.boolean(),
  scraperCoverage: z.number().int(),
  sample: z.array(z.object({ id: z.string(), url: z.string() })).max(20),
});

/** Current effective values of the two retention operational switches. */
export const jobRetentionFlagsSchema = z.object({
  dryRun: z.boolean(),
  disabled: z.boolean(),
});

export type JobRetentionFlags = z.infer<typeof jobRetentionFlagsSchema>;

export const jobRetentionStatsSchema = z.object({
  lastRun: z
    .object({
      startedAt: z.string().datetime(),
      finishedAt: z.string().datetime().nullable(),
      dryRun: z.boolean(),
      status: z.enum(["RUNNING", "SUCCESS", "FAILED", "HALTED"]),
      proposed: z.number().int(),
      archived: z.number().int(),
      hardDeleted: z.number().int(),
      protected: z.number().int(),
      durationMs: z.number().int().nullable(),
      perBoard: jobRetentionPerBoardCounts,
      unseenProposal: z.record(z.string(), unseenProposalBoard).nullable(),
    })
    .nullable(),
  last24h: jobRetentionRollup,
  lifetime: jobRetentionRollup,
  current: z.object({ active: z.number().int(), archived: z.number().int() }),
  // Current effective operational switches — read off the 2FA-guarded stats
  // endpoint so the admin card derives its mode without the unguarded
  // admin-flags GET.
  flags: jobRetentionFlagsSchema,
});

export class JobRetentionStatsDto extends createZodDto(jobRetentionStatsSchema) {}

export type JobRetentionStatsResponse = z.infer<typeof jobRetentionStatsSchema>;

// ── Job retention approve / go-live (2FA-gated, audited) ──────────────
// At least one of the two switches must be present; both may be flipped in
// one call. Response echoes the new effective flag state.

export const jobRetentionApproveSchema = z
  .object({
    dryRun: z.boolean().optional(),
    disabled: z.boolean().optional(),
  })
  .refine((data) => data.dryRun !== undefined || data.disabled !== undefined, {
    message: "At least one of 'dryRun' or 'disabled' must be provided.",
  });

export class JobRetentionApproveDto extends createZodDto(jobRetentionApproveSchema) {}

export type JobRetentionApproveResponse = JobRetentionFlags;

// ── Manual archive approval ───────────────────────────────────────────
// Archiving is a human decision. The worker gathers liveness evidence and
// proposes; an admin approves an explicit list of ids. `evidence` reports
// whether the last sweep is trustworthy enough to act on at all — a sweep that
// was mostly inconclusive means a board blocked us, not that jobs are gone.

export const archiveEvidenceHealthSchema = z.object({
  ok: z.boolean(),
  reason: z.string().nullable(),
  lastSweepAt: z.string().nullable(),
  checked: z.number().int(),
  inconclusiveRate: z.number(),
});

export const archiveProposalRowSchema = z.object({
  id: z.string(),
  board: z.string(),
  title: z.string(),
  company: z.string(),
  url: z.string(),
  createdAt: z.string(),
  ageDays: z.number().int(),
  livenessStatus: z.string().nullable(),
  lastLivenessCheckAt: z.string().nullable(),
  livenessDeadStreak: z.number().int(),
  confirmedDead: z.boolean(),
});

export const archiveProposalsSchema = z.object({
  rows: z.array(archiveProposalRowSchema),
  totalEligible: z.number().int(),
  confirmedDeadCount: z.number().int(),
  evidence: archiveEvidenceHealthSchema,
});

export type ArchiveProposalsResponse = z.infer<typeof archiveProposalsSchema>;
export type ArchiveProposalRow = z.infer<typeof archiveProposalRowSchema>;

/** Explicit ids only — there is deliberately no "approve everything" shape. */
export const archiveApproveSchema = z.object({
  jobIds: z.array(z.string()).min(1),
});

export class ArchiveApproveDto extends createZodDto(archiveApproveSchema) {}

export const archiveApproveResponseSchema = z.object({
  archived: z.number().int(),
  skipped: z.number().int(),
  perBoard: z.record(z.number().int()),
  approvalId: z.string(),
});

export type ArchiveApproveResponse = z.infer<typeof archiveApproveResponseSchema>;

// ── Profile scrape request / response ─────────────────────────────────

export const scrapeProfilesRequestSchema = z.object({
  profileIds: z.array(z.string()).min(1),
});

export class ScrapeProfilesRequestDto extends createZodDto(scrapeProfilesRequestSchema) {}

export type ScrapeProfilesRequest = z.infer<typeof scrapeProfilesRequestSchema>;

export type ScrapeProfilesResponse = {
  triggered: number;
  skipped: number;
  profiles: {
    id: string;
    name: string;
    triggered: boolean;
  }[];
};

// ── Durable discovery board health (T031) ────────────────────────────
export const discoveryHealthRowSchema = z.object({
  board: z.string(),
  measured: z.boolean(),
  health: z.enum(["healthy", "degraded", "unknown", "unmeasured"]),
  reason: z.string().nullable(),
  lastAttemptAt: z.string().datetime().nullable(),
  lastSuccessAt: z.string().datetime().nullable(),
  oldestPendingAt: z.string().datetime().nullable(),
  outcomes24h: z.object({
    SUCCEEDED: z.number().int().nonnegative(),
    PARTIAL: z.number().int().nonnegative(),
    FAILED: z.number().int().nonnegative(),
    SKIPPED: z.number().int().nonnegative(),
  }),
  unmeasuredV0Count: z.number().int().nonnegative(),
});

export const discoveryHealthResponseSchema = z.object({
  generatedAt: z.string().datetime(),
  windowHours: z.number().int(),
  rows: z.array(discoveryHealthRowSchema),
});

export type DiscoveryHealthRow = z.infer<typeof discoveryHealthRowSchema>;
export type DiscoveryHealthResponse = z.infer<typeof discoveryHealthResponseSchema>;
